<script>
import AspectRatio from './AspectRatio.vue'
import { useOptimizedImage, fitTypes, cropTypes } from '@monogrid/vue3-boost/functions/useOptimizedImage'
import { computed, defineComponent, ref, watch } from 'vue'

export default defineComponent({
  components: { AspectRatio },
  props: {
    image: {
      type: Object,
      required: true,
      validator: value => typeof value.id === 'string' && typeof value.metaData === 'string'
    },
    tag: {
      type: String,
      required: false,
      default () { return 'picture' }
    },
    format: {
      type: String,
      required: false,
      validator: value => ['webp', 'jpg', 'png'].includes(value),
      default: null
    },
    contain: { type: Boolean, required: false },
    width: { type: Number, required: false, default: null },
    height: { type: Number, required: false, default: null },
    maxWidth: {
      type: [Number, String],
      required: false,
      validator: value => typeof value === 'number' || value === 'breakpoint',
      default: null
    },
    maxHeight: { type: Number, required: false, default: null },
    quality: { type: Number, required: false, default: null },
    blur: { type: Number, required: false, default: null },
    fit: {
      type: String,
      required: false,
      validator: value => fitTypes.includes(value),
      default: null
    },
    crop: {
      type: String,
      required: false,
      validator: value => cropTypes.includes(value),
      default: null
    },
    fillColor: {
      type: String,
      required: false,
      default: null
    },
    focalPoint: {
      type: Object,
      required: false,
      validator ({ x, y } = {}) {
        return typeof x === 'number' && typeof y === 'number' && x > 0 && x < 1 && y > 0 && y < 1
      },
      default: null
    },
    aspectRatio: {
      type: [String, Number],
      required: false,
      default: null
    }
  },
  setup (props) {
    const optimizedImageParams = computed(() => {
      if (props.contain) {
        // we want the full photo, not cropped
        // so we delete
        return { ...props, height: null, aspectRatio: null }
      }
      // replace 16/9 or 16-9 sytax with 16:9
      const aspectRatio = typeof props.aspectRatio === 'string' ? props.aspectRatio.replace(/(\/|-)/g, ':') : props.aspectRatio
      return { ...props, aspectRatio }
    })

    const { url, lazySrc, width, height } = useOptimizedImage(props.image, optimizedImageParams)

    const loaded = ref(false)

    watch(url, () => {
      loaded.value = false
    })

    const pictureStyle = computed(() => {
      return {
        backgroundImage: loaded.value ? null : `url(${lazySrc.value})`
      }
    })

    const computedAspectRatio = computed(() => {
      if (props.width && props.height) {
        return props.width / props.height
      }
      return props.aspectRatio || (width.value / height.value)
    })

    return {
      loaded,
      pictureStyle,
      computedAspectRatio,
      url
    }
  }
})
</script>

<template>
  <AspectRatio
    :tag="tag"
    :aspect-ratio="computedAspectRatio"
    :style="pictureStyle"
    class="optimized-image"
    :class="{loaded, contain}"
  >
    <img
      :key="url"
      :src="url"
      decoding="async"
      loading="lazy"
      @load="loaded = true"
    />
  </AspectRatio>
</template>

<style lang="scss">
.optimized-image {
  display: flex;
  flex-direction: column;
  background-position: 50% 50%;
  background-repeat: no-repeat;
  background-size: cover;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.contain {
    background-size: contain;

    img {
      object-fit: contain;
    }
  }
}
</style>
